import { Directive, HostListener, Input, ElementRef, Renderer2, OnInit } from '@angular/core';
import { Router } from '@angular/router';

/**
 * @description
 * A directive that enables Angular's `router.navigate()` functionality on `<a>` elements while maintaining native link behavior.
 *
 * Clicking the link will navigate within the application without a full-page reload.
 * Ctrl+Click, Cmd+Click (Mac), and middle-click open the link in a new tab as expected.
 *
 * The directive automatically adds the `href` attribute to ensure proper styling and behavior.
 *
 * @Input appRouterLinkEnhanced (required) - The route to navigate to when clicked.
 *
 * @example
 * <!-- Navigates within the app without reloading -->
 * <a appRouterLinkEnhanced="/dashboard">Dashboard</a>
 *
 * @example
 * <!-- Binds a dynamic link -->
 * <a [appRouterLinkEnhanced]="dynamicLink">Go to page</a>
 */

@Directive({
    selector: '[appRouterLinkEnhanced]',
    standalone: true,
})
export class RouterLinkEnhancedDirective implements OnInit {
    @Input('appRouterLinkEnhanced') linkUrl!: string;

    constructor(
        private router: Router,
        private el: ElementRef,
        private renderer: Renderer2,
    ) {}

    @HostListener('click', ['$event'])
    onClick(event: MouseEvent): void {
        if (!this.linkUrl) {
            return;
        }

        // For Ctrl (Windows/Linux), Command (Mac) or middle-click, the default behavior is allowed
        if (event.ctrlKey || event.metaKey || event.button === 1) {
            return;
        }

        // Prevent full page reload from [href] default behaviour
        event.preventDefault();

        // Navigate using Angular router
        this.router.navigate([this.linkUrl]);
    }

    ngOnInit() {
        // Ensures the element behaves like a link
        this.renderer.setAttribute(this.el.nativeElement, 'href', this.linkUrl);
        this.renderer.setStyle(this.el.nativeElement, 'cursor', 'pointer');
    }
}
